import { useRef } from 'react';
import type { ReactNode } from 'react';
import { IFrame, type IFrameHandle, type IFrameSignalReason } from '@proposify/ui';
import { EMBED_PROVIDER_CONFIG, extractEmbedSrc, type EmbedSubtype } from './embed';
import { resolveAllowedOrigin } from './resolveAllowedOrigin';
import { useHandleEmbedFailure } from './useHandleEmbedFailure';

type Props = {
  embedUrl: string;
  subtype: EmbedSubtype;
  blockId: string;
  width?: string | number;
  height?: string | number;
  renderFallback?: (retry: () => void) => ReactNode;
  onBrokenChange?: (isBroken: boolean) => void;
};

const EmbedIframe = ({ embedUrl, subtype, blockId, width = '100%', height = '100%', renderFallback, onBrokenChange }: Props) => {
  const src = extractEmbedSrc(embedUrl);
  const iframeRef = useRef<IFrameHandle>(null);
  const handleEmbedFailure = useHandleEmbedFailure(blockId, subtype);

  const retry = () => {
    onBrokenChange?.(false);
    iframeRef.current?.reset();
  };

  if (!src) return <>{renderFallback?.(retry)}</>;

  const providerConfig = EMBED_PROVIDER_CONFIG[subtype];

  const handleSignal = (reason: IFrameSignalReason, origin: string | null) => {
    onBrokenChange?.(true);
    handleEmbedFailure(reason, origin);
  };

  return (
    <IFrame
      ref={iframeRef}
      src={src}
      allowedOrigins={resolveAllowedOrigin(src)}
      title={providerConfig.providerName}
      sandbox={providerConfig.sandbox}
      width={width}
      height={height}
      onSignal={handleSignal}
      fallback={renderFallback?.(retry)}
    />
  );
};

export default EmbedIframe;
